import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import {
  ApiProject,
  CATEGORY_LABELS,
  FEATURED_PROJECTS,
  getPlatformLinks,
  normalizeProject,
  Project,
  ProjectScreen,
  PROJECTS_API_URL,
} from './projectData';
import './ProjectDetail.scss';

type NextProject = { id: number; title: string } | null;

type LoadState = 'loading' | 'ready' | 'notFound';

interface Section {
  id: string;
  label: string;
}

interface ScreenGroup extends Section {
  title: string;
  screens: ProjectScreen[];
  phone: boolean;
}

const getHost = (url: string) => {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const hasAppLink = (project: Project) => Boolean(project.androidUrl || project.iosUrl);
const hasAnyLink = (project: Project) => Boolean(project.webUrl) || hasAppLink(project);

// 플랫폼 링크가 입력돼 있으면 그 값 기준으로, 없으면 platform 문구로 판단
const hasMobile = (project: Project) =>
  hasAnyLink(project) ? hasAppLink(project) : /ios|android|app|앱|모바일/i.test(project.platform);
const hasWeb = (project: Project) =>
  hasAnyLink(project)
    ? Boolean(project.webUrl)
    : !project.platform || /web|웹/i.test(project.platform);

const BrowserMock: React.FC<{ project: Project }> = ({ project }) => (
  <div className="pd-browser">
    <div className="pd-browser__bar">
      <i />
      <i />
      <i />
      <span>{getHost(project.webUrl) || 'yeplat.com'}</span>
    </div>
    {project.thumbnailUrl ? (
      <img className="pd-browser__img" src={project.thumbnailUrl} alt={`${project.title} 화면`} />
    ) : (
      <div className="pd-web">
        <div className="pd-web__head">
          <span className="pd-web__logo">{project.title}</span>
          <span className="pd-web__meta">{CATEGORY_LABELS[project.category]}</span>
        </div>
        <div className="pd-web__hero" />
        <div className="pd-web__row">
          {[0, 1, 2].map((key) => (
            <div key={key} className="pd-web__card">
              <i />
              <u />
              <u />
            </div>
          ))}
        </div>
        <div className="pd-web__list">
          {project.techStack.slice(0, 3).map((tech) => (
            <div key={tech} className="pd-web__li">
              <i />
              <div>
                <b />
                <b />
              </div>
              <u>{tech}</u>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const PhoneMock: React.FC<{ project: Project; imageUrl?: string }> = ({ project, imageUrl }) => (
  <div className="pd-phone">
    <div className="pd-phone__sc">
      {imageUrl ? (
        <img src={imageUrl} alt={`${project.title} 앱 화면`} />
      ) : (
        <>
          <div className="pd-phone__st">
            <span>9:41</span>
            <i>
              <b />
              <b />
            </i>
          </div>
          <div className="pd-app">
            <div className="pd-app__hi">
              {project.title}
              <small>{project.headline || CATEGORY_LABELS[project.category]}</small>
            </div>
            <div className="pd-app__hero">
              <b>{project.techStack[0] || 'Mobile App'}</b>
              <em>{project.platform || 'iOS · Android'}</em>
              <u />
            </div>
            <div className="pd-app__list">
              {[0, 1].map((key) => (
                <div key={key} className="pd-app__li">
                  <i />
                  <div>
                    <b />
                    <b />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="pd-app__tabs">
            <i className="on" />
            <i />
            <i />
            <i />
          </div>
        </>
      )}
    </div>
  </div>
);

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [next, setNext] = useState<NextProject>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [activeSection, setActiveSection] = useState(0);
  const [progress, setProgress] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    window.scrollTo(0, 0);

    const fetchProject = async () => {
      setState('loading');

      // 클라이언트에만 있는 대표 프로젝트(음수 id)는 API에 없으므로 로컬 데이터로 바로 표시
      const featuredIndex = FEATURED_PROJECTS.findIndex((item) => String(item.id) === id);
      if (featuredIndex !== -1) {
        const featuredNext = FEATURED_PROJECTS[(featuredIndex + 1) % FEATURED_PROJECTS.length];
        setProject(FEATURED_PROJECTS[featuredIndex]);
        setNext(featuredNext.id !== FEATURED_PROJECTS[featuredIndex].id ? featuredNext : null);
        setState('ready');
        return;
      }

      try {
        const response = await fetch(`${PROJECTS_API_URL}/${id}`, { signal: controller.signal });

        if (response.status === 404) {
          setProject(null);
          setState('notFound');
          return;
        }

        if (!response.ok) {
          throw new Error(`Project API responded with ${response.status}`);
        }

        const data: ApiProject = await response.json();
        setProject(normalizeProject(data));
        setNext(data.next || null);
        setState('ready');
      } catch (error) {
        if ((error as Error).name === 'AbortError') return;

        console.error('Failed to fetch project:', error);
        setProject(null);
        setState('notFound');
      }
    };

    fetchProject();

    return () => controller.abort();
  }, [id]);

  const overviewCards = useMemo(() => {
    if (!project) return [];

    const cards = [
      { key: 'challenge', label: '과제', text: project.challenge },
      { key: 'approach', label: '접근', text: project.approach },
      { key: 'result', label: '결과', text: project.result },
    ].filter((card) => card.text);

    return cards.length ? cards : [{ key: 'intro', label: '소개', text: project.description }];
  }, [project]);

  // 화면에 kind 가 지정돼 있으면 웹 / 앱 갤러리로 나누고, 없으면 기존처럼 하나로 보여준다
  const screenGroups = useMemo<ScreenGroup[]>(() => {
    if (!project || !project.screens.length) return [];

    if (!project.screens.some((screen) => screen.kind)) {
      return [
        {
          id: 'screens',
          label: '주요 화면',
          title: '주요 화면',
          screens: project.screens,
          phone: hasMobile(project) && !hasWeb(project),
        },
      ];
    }

    const webScreens = project.screens.filter((screen) => screen.kind !== 'mobile');
    const appScreens = project.screens.filter((screen) => screen.kind === 'mobile');

    return [
      { id: 'screens-web', label: '웹 화면', title: '웹 주요 화면', screens: webScreens, phone: false },
      { id: 'screens-app', label: '앱 화면', title: '앱 주요 화면', screens: appScreens, phone: true },
    ].filter((group) => group.screens.length);
  }, [project]);

  const sections = useMemo<Section[]>(() => {
    if (!project) return [];

    const list: Section[] = [{ id: 'overview', label: '개요' }];
    screenGroups.forEach((group) => list.push({ id: group.id, label: group.label }));
    if (project.techStack.length) list.push({ id: 'stack', label: '기술 스택' });
    return list;
  }, [project, screenGroups]);

  const metaItems = useMemo(() => {
    if (!project) return [];

    return [
      { label: 'Type', value: CATEGORY_LABELS[project.category] },
      { label: 'Client', value: project.client },
      { label: 'Industry', value: project.industry },
      { label: 'Period', value: project.period },
      { label: 'Scope', value: project.scope },
      { label: 'Platform', value: project.platform },
    ].filter((item) => item.value);
  }, [project]);

  // 스크롤 진행률 + 목차 하이라이트
  useEffect(() => {
    if (state !== 'ready') return undefined;

    const onScroll = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, y / max) : 0);

      const pos = y + Math.min(window.innerHeight * 0.35, 320);
      let current = 0;
      sections.forEach((section, index) => {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top + y <= pos) current = index;
      });
      setActiveSection(current);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [state, sections]);

  // 스크롤 등장 애니메이션
  useEffect(() => {
    if (state !== 'ready' || !rootRef.current) return undefined;

    const items = Array.from(rootRef.current.querySelectorAll<HTMLElement>('[data-reveal]'));

    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );

    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [state, project]);

  const scrollToSection = (event: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    const target = document.getElementById(sectionId);
    if (!target) return;

    event.preventDefault();
    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - 110,
      behavior: 'smooth',
    });
  };

  const renderStatus = (message: string) => (
    <div className="pd-status">
      <p>{message}</p>
      {state === 'notFound' ? (
        <Link className="pd-btn" to="/projects">
          포트폴리오 목록으로 →
        </Link>
      ) : null}
    </div>
  );

  const renderDetail = (item: Project) => {
    const showWeb = hasWeb(item);
    const showPhone = hasMobile(item);
    const platformLinks = getPlatformLinks(item);
    // 웹·앱을 함께 보여줄 때 폰 목업에는 첫 번째 앱 화면을 사용
    const heroPhoneImage = showWeb
      ? item.screens.find((screen) => screen.kind === 'mobile')?.imageUrl
      : item.thumbnailUrl;

    return (
      <>
        <div className="pd-progress" aria-hidden="true">
          <span style={{ width: `${progress * 100}%` }} />
        </div>

        {/* ============ HERO ============ */}
        <section className="pd-hero">
          <div className="pd-hero__grid" aria-hidden="true" />
          <div className="pd-hero__glow" aria-hidden="true" />
          <div className="pd-wrap">
            <div className="pd-crumbs">
              <Link to="/projects">포트폴리오</Link>
              <span>/</span>
              <b>{CATEGORY_LABELS[item.category]}</b>
            </div>

            <p className="pd-eyebrow pd-eyebrow--accent">Case Study — {item.title}</p>
            <h1 className="pd-hero__title">{item.headline || item.title}</h1>
            <p className="pd-hero__lead">{item.description}</p>

            {platformLinks.length ? (
              <div className="pd-hero__links">
                {platformLinks.map((link) => (
                  <a
                    key={link.key}
                    className={`pd-btn pd-hero__link pd-hero__link--${link.key}`}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label} ↗
                  </a>
                ))}
              </div>
            ) : null}

            {metaItems.length ? (
              <dl className="pd-meta">
                {metaItems.map((meta) => (
                  <div key={meta.label} className="pd-meta__i">
                    <dt>{meta.label}</dt>
                    <dd>{meta.value}</dd>
                  </div>
                ))}
              </dl>
            ) : null}

            <div className="pd-shot" data-reveal>
              <div
                className={`pd-shot__panel ${showWeb && showPhone ? '' : 'pd-shot__panel--single'}`}
              >
                <span className="pd-shot__cap">
                  {showWeb && showPhone ? 'Web & Mobile App' : showPhone ? 'Mobile App' : 'Web Platform'}
                </span>
                {showWeb ? <BrowserMock project={item} /> : null}
                {showPhone ? (
                  <div className="pd-phone-stack">
                    <PhoneMock project={item} imageUrl={heroPhoneImage} />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        {/* ============ BODY ============ */}
        <div className="pd-body">
          <aside className="pd-rail" aria-label="섹션 목차">
            <div className="pd-rail__t">Contents</div>
            <nav>
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className={activeSection === index ? 'is-active' : ''}
                  onClick={(event) => scrollToSection(event, section.id)}
                >
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  {section.label}
                </a>
              ))}
            </nav>
            <div className="pd-rail__bar">
              <i style={{ width: `${progress * 100}%` }} />
            </div>
            {item.client || item.period ? (
              <div className="pd-rail__foot">
                {item.client ? (
                  <>
                    Client
                    <br />
                    {item.client}
                  </>
                ) : null}
                {item.client && item.period ? (
                  <>
                    <br />
                    <br />
                  </>
                ) : null}
                {item.period ? (
                  <>
                    Period
                    <br />
                    {item.period}
                  </>
                ) : null}
              </div>
            ) : null}
          </aside>

          <div className="pd-content">
            {sections.map((section, index) => {
              const no = String(index + 1).padStart(2, '0');

              if (section.id === 'overview') {
                return (
                  <section key={section.id} className="pd-sec" id="overview">
                    <div className="pd-sec__head" data-reveal>
                      <p className="pd-eyebrow">{no} — 개요</p>
                      <h2 className="pd-h-sec">
                        한 장으로 보는
                        <br />이 프로젝트
                      </h2>
                    </div>
                    <div className={`pd-cards pd-cards--${overviewCards.length}`}>
                      {overviewCards.map((card, cardIndex) => (
                        <article
                          key={card.key}
                          className={`pd-card ${
                            cardIndex === overviewCards.length - 1 && overviewCards.length > 1
                              ? 'pd-card--accent'
                              : ''
                          }`}
                          data-reveal
                          style={{ '--d': `${cardIndex * 90}ms` } as React.CSSProperties}
                        >
                          <b>{card.label}</b>
                          <p>{card.text}</p>
                        </article>
                      ))}
                    </div>
                  </section>
                );
              }

              const group = screenGroups.find((screenGroup) => screenGroup.id === section.id);

              if (group) {
                return (
                  <section key={section.id} className="pd-sec" id={group.id}>
                    <div className="pd-sec__head" data-reveal>
                      <p className="pd-eyebrow">
                        {no} — {group.label}
                      </p>
                      <h2 className="pd-h-sec">{group.title}</h2>
                    </div>
                    <div className="pd-panel" data-reveal>
                      <div className="pd-panel__grid" aria-hidden="true" />
                      <div className="pd-panel__in">
                        {group.screens.length > 1 ? (
                          <div className="pd-gal__hint-row">
                            <span className="pd-gal__hint">가로로 스크롤 →</span>
                          </div>
                        ) : null}
                        <div className={`pd-gal ${group.phone ? '' : 'pd-gal--wide'}`}>
                          {group.screens.map((screen, screenIndex) => (
                            <figure key={`${screen.imageUrl}-${screenIndex}`}>
                              {group.phone ? (
                                <PhoneMock project={item} imageUrl={screen.imageUrl} />
                              ) : (
                                <div className="pd-gal__img">
                                  <img src={screen.imageUrl} alt={screen.caption || `${item.title} 화면`} />
                                </div>
                              )}
                              <figcaption>
                                <b>{String(screenIndex + 1).padStart(2, '0')}</b> {screen.caption}
                              </figcaption>
                            </figure>
                          ))}
                        </div>
                      </div>
                    </div>
                  </section>
                );
              }

              return (
                <section key={section.id} className="pd-sec" id="stack">
                  <div className="pd-sec__head" data-reveal>
                    <p className="pd-eyebrow">{no} — 기술 스택</p>
                    <h2 className="pd-h-sec">사용한 기술</h2>
                  </div>
                  <ul className="pd-stack" data-reveal>
                    {item.techStack.map((tech) => (
                      <li key={tech}>{tech}</li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </div>

        {next ? (
          <div className="pd-next">
            <Link to={`/projects/${next.id}`} data-reveal>
              <div>
                <span className="pd-next__k">Next Project</span>
                <h3>
                  다음 프로젝트 — <span className="u">{next.title}</span>
                </h3>
              </div>
              <span className="pd-next__ar">→</span>
            </Link>
          </div>
        ) : null}

        <section className="pd-cta-wrap">
          <div className="pd-wrap">
            <div className="pd-cta" data-reveal>
              <h2>
                비슷한 문제를
                <br />
                풀고 계신가요?
                <br />
                <span className="u">이야기해 주세요.</span>
              </h2>
              <Link className="pd-btn" to="/inquiry">
                프로젝트 문의하기 →
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  };

  return (
    <div className="project-detail" ref={rootRef}>
      <Header />
      <main>
        {state === 'loading' ? renderStatus('프로젝트를 불러오는 중입니다...') : null}
        {state === 'notFound' ? renderStatus('프로젝트를 찾을 수 없습니다.') : null}
        {state === 'ready' && project ? renderDetail(project) : null}
      </main>
      <Footer />
    </div>
  );
};

export default ProjectDetail;
